import { observable } from 'mobx';
import type { ReactNode } from 'react';

interface DialogEntry {
  readonly id: number;
  readonly node: ReactNode;
}

/**
 * Singleton MobX-backed store of currently-mounted dialogs. Mutated via
 * `show()` (returns a handle to remove the dialog later) and rendered
 * by `<DialogMount />` somewhere stable in the app tree.
 *
 * Typically consumed via the higher-level `waitDialog<T>()` /
 * `showDialog()` helpers in `./waitDialog`. Direct access is exposed
 * for advanced cases (e.g., force-clearing on logout).
 *
 * Stores the `ReactNode` directly rather than a render thunk: callers
 * resolve their JSX once at `show()` time (capturing stable callbacks),
 * and `<DialogMount />` re-renders just by re-using the stored node.
 */
class DialogStore {
  private readonly _dialogs = observable.array<DialogEntry>([], {
    deep: false,
  });
  private _nextId = 1;

  get dialogs(): readonly DialogEntry[] {
    return this._dialogs;
  }

  /**
   * Mount a dialog. The returned `close` removes it from the store —
   * call it when the dialog resolves (Save/Cancel) or when an external
   * caller wants to dismiss it programmatically.
   */
  show(node: ReactNode): { close: () => void } {
    const entry: DialogEntry = { id: this._nextId++, node };
    this._dialogs.push(entry);
    return {
      close: () => {
        this._dialogs.remove(entry);
      },
    };
  }

  /** Remove every dialog. Intended for tests and for app-level resets. */
  clear(): void {
    this._dialogs.clear();
  }
}

export const dialogStore = new DialogStore();
